/* eslint-disable no-console */
import {
  getDataWithComp,
  getAlert,
  insertData,
  deleteData,
  updateData,
} from "./SamuiUtils";
import { manageWhItemData } from "./WarehouseUtils";

// -------- helpers --------
const trim = (s) => String(s ?? "").trim();
const q = (s = "") => `'${String(s ?? "").replaceAll("'", "''")}'`;
const isNonEmpty = (s) => trim(s).length > 0;

const normalizeName = (s) =>
  String(s ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

/**
 * ใช้กับ <ProductModal onSubmit={submitItems} />
 * payload = { header:{...}, details:[...] }
 * สัญญา: ไม่ throw ออกนอก คืน { ok:true } หรือ { ok:false, errorCode, ... } เสมอ
 */
export async function submitItems(payload) {
  const { header = {}, details = [], mode = "create" } = payload || {};
  const compId = window.localStorage.getItem("company");
  const createdBy = window.localStorage.getItem("name") || "";
  const getSqlDateTime = () => {
    const now = new Date();
    return new Date(now.getTime() - now.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 19)
      .replace("T", " ");
  };

  // ---------- guard ----------
  if (!Array.isArray(details) || details.length === 0) {
    const r = {
      ok: false,
      errorCode: "NO_DETAILS",
      message: "ไม่มีรายละเอียดสินค้า",
    };
    getAlert?.("FAILED", r.message);
    return r;
  }
  if (!compId) {
    const r = {
      ok: false,
      errorCode: "NO_COMPANY",
      message: "ไม่พบ Company ID (compId)",
    };
    getAlert?.("FAILED", r.message);
    return r;
  }
  const missingCode = details.find((d) => !isNonEmpty(d.Item_Code));
  if (missingCode) {
    const r = {
      ok: false,
      errorCode: "MISSING_CODE",
      message: "บางรายการไม่มี Item_Code",
    };
    getAlert?.("FAILED", r.message);
    return r;
  }

  try {
    // ---------- 1) ตรวจซ้ำใน payload ----------
    const codeSet = new Set();
    const bcSet = new Set();
    const nameSet = new Set(); // ใช้ยิง IN ใน DB (แบบเดิม)
    const nameNormSet = new Set(); // กันซ้ำใน payload แบบ normalize

    for (const d of details) {
      const code = trim(d.Item_Code);
      const bc = trim(d.Item_Barcode);

      const fullName = trim(d.Item_Name);

      if (code) {
        if (codeSet.has(code)) {
          const r = { ok: false, errorCode: "DUP_CODE_IN_PAYLOAD", code };
          getAlert?.("FAILED", `พบ Item_Code ซ้ำในข้อมูลที่ส่งมา: ${code}`);
          return r;
        }
        codeSet.add(code);
      }

      if (bc) {
        if (bcSet.has(bc)) {
          const r = {
            ok: false,
            errorCode: "DUP_BARCODE_IN_PAYLOAD",
            barcode: bc,
          };
          getAlert?.("FAILED", `พบ Item_Barcode ซ้ำในข้อมูลที่ส่งมา: ${bc}`);
          return r;
        }
        bcSet.add(bc);
      }

      const nmNorm = normalizeName(fullName);
      if (nmNorm) {
        if (nameNormSet.has(nmNorm)) {
          getAlert?.(
            "FAILED",
            `พบชื่อสินค้า(ชื่อเต็ม)ซ้ำในข้อมูลที่ส่งมา: ${fullName}`
          );
          return {
            ok: false,
            errorCode: "DUP_FULLNAME_IN_PAYLOAD",
            name: fullName,
          };
        }
        nameNormSet.add(nmNorm);
      }

      if (fullName) nameSet.add(fullName);
    }

    // ---------- 2) ตรวจซ้ำใน DB ----------
    const whereParts = [];
    if (codeSet.size)
      whereParts.push(`Item_Code IN (${[...codeSet].map(q).join(",")})`);
    if (bcSet.size)
      whereParts.push(`Item_Barcode IN (${[...bcSet].map(q).join(",")})`);
    if (nameSet.size)
      whereParts.push(`Item_Name IN (${[...nameSet].map(q).join(",")})`);

    if (whereParts.length) {
      const dup = await getDataWithComp(
        "Tb_Set_Item",
        `AND (${whereParts.join(" OR ")})`
      );

      if (dup?.length) {
        // ✅ Edit: ยกเว้นตัวเอง (Item_Id ที่อยู่ใน details)
        const editingIds = new Set(
          details
            .map((d) => d.Item_Id)
            .filter((id) => id != null && id !== undefined)
        );

        const conflicts = dup
          .filter((r) => {
            if (mode === "edit" && editingIds.has(r.Item_Id)) return false;
            return true;
          })
          .map((r) => {
            const hits = [];
            if (codeSet.has(trim(r.Item_Code))) hits.push("Item_Code");
            if (bcSet.has(trim(r.Item_Barcode))) hits.push("Item_Barcode");
            if (nameSet.has(trim(r.Item_Name))) hits.push("Item_Name");

            return {
              Item_Id: r.Item_Id,
              Item_Code: r.Item_Code,
              Item_Barcode: r.Item_Barcode,
              Item_Short_Name: r.Item_Short_Name,
              Item_Name: r.Item_Name,
              conflictFields: hits,
              Created_Date: r.Created_Date,
              Created_By_Name: r.Created_By_Name,
              Update_Date: r.Update_Date,
              Update_By_Name: r.Update_By_Name,
            };
          })
          // ถ้าบาง record หลุดมาแต่ไม่ได้ชน field ไหนเลย ก็ไม่ต้องโชว์
          .filter((x) => x.conflictFields.length > 0);

        if (conflicts.length > 0) {
          return { ok: false, errorCode: "DUPLICATE", conflicts };
        }
      }
    }

    // ---------- 3) เตรียม insert ----------
    const fieldsBase = [
      "Comp_Id",
      "Item_Barcode",
      "Item_Code",
      "Item_Name",
      "Item_Name_EN",
      "Item_Short_Name",
      "Item_Unit_IN",
      "Item_Qty",
      "Item_Is_Sale",
      "Item_Is_Service",
      "Item_Cost",
      "Item_Price",
      "Item_PriceA",
      "Item_PriceB",
      "Item_Brand",
      "Item_Series",
      "Item_Group",
      "Item_Colour",
      "Item_Size",
      "Item_Status",
    ];
    const fieldsWithAudit = [
      ...fieldsBase,
      "Created_Date",
      "Created_By_Name",
    ];

    const buildRowValuesBase = (row) => {
      const shortName = trim(header.Item_Name) || null;

      console.debug("🔍 buildRowValues debug:", {
        Item_Code: row.Item_Code,
        raw_Size: row.Size,
        raw_Item_Size: row.Item_Size,
        raw_Colour: row.Colour,
        raw_Item_Colour: row.Item_Colour,
        trimmed_Size: trim(row.Item_Size || row.Size),
        trimmed_Colour: trim(row.Item_Colour || row.Colour),
        final_Size: trim(row.Item_Size || row.Size) || "",
        final_Colour: trim(row.Item_Colour || row.Colour) || "",
      });

      return [
        Number(compId),
        trim(row.Item_Barcode) || null,
        trim(row.Item_Code) || null,
        trim(row.Item_Name) || null,
        trim(row.Item_Name) || null,
        shortName,
        trim(header.Item_Unit_IN) || null,
        Number(row.Item_Qty ?? header.Item_Qty ?? 0),
        trim(row.Item_Is_Sale ?? header.Item_Is_Sale ?? "N"),
        trim(row.Item_Is_Service ?? header.Item_Is_Service ?? "N"),
        Number(row.Item_Cost ?? header.Item_Cost ?? 0),
        Number(row.Item_Price ?? header.Item_Price ?? 0),
        Number(row.Item_PriceA ?? header.Item_PriceA ?? 0),
        Number(row.Item_PriceB ?? header.Item_PriceB ?? 0),
        trim(row.Item_Brand || header.Item_Brand) || "",
        trim(row.Item_Series || header.Item_Series) || "",
        Number(header.Item_Group),
        trim(row.Item_Colour || row.Colour) || "",
        trim(row.Item_Size || row.Size) || "",
        trim(row.Item_Status ?? header.Item_Status ?? "Y"),
      ];
    };
    const buildRowValuesWithAudit = (row) => [
      ...buildRowValuesBase(row),
      getSqlDateTime(),
      createdBy || null,
    ];

    let valuesMatrix = details.map(buildRowValuesWithAudit);

    // ---------- 4) bulk insert ----------
    const CHUNK = 200;
    let bulkOk = true;
    const runInsert = async (fields, matrix) => {
      for (let i = 0; i < matrix.length; i += CHUNK) {
        const chunk = matrix.slice(i, i + CHUNK);
        const ins = await insertData("Tb_Set_Item", fields.join(","), chunk);
        if (!ins || ins.ok === false || ins.status === "FAILED") {
          return ins || { status: "FAILED" };
        }
      }
      return { ok: true };
    };

    let insertResult = await runInsert(fieldsWithAudit, valuesMatrix);
    const isInvalidColumn =
      insertResult?.status === "FAILED" &&
      /Invalid column name/i.test(insertResult?.message || "");
    if (isInvalidColumn) {
      valuesMatrix = details.map(buildRowValuesBase);
      insertResult = await runInsert(fieldsBase, valuesMatrix);
    }
    if (!insertResult || insertResult.ok === false || insertResult.status === "FAILED") {
      bulkOk = false;
    }

    // ---------- 5) ตรวจครบ / fallback insert ทีละแถว ----------
    const codesArray = [...codeSet];
    const codesSql = codesArray.length ? codesArray.map(q).join(",") : null;

    let inserted = codesSql
      ? await getDataWithComp(
          "Tb_Set_Item",
          `AND Comp_Id=${compId} AND Item_Code IN (${codesSql})`
        )
      : [];

    // ✅ ใช้ trim() ให้ consistent กันทั้งสองฝั่ง
    const haveCodes = new Set((inserted || []).map((r) => String(trim(r.Item_Code || ""))));
    if (!bulkOk || haveCodes.size !== codeSet.size) {
      const missing = details.filter(
        (d) => !haveCodes.has(String(trim(d.Item_Code || "")))
      );
      for (const row of missing) {
        const ins = await insertData(
          "Tb_Set_Item",
          (isInvalidColumn ? fieldsBase : fieldsWithAudit).join(","),
          [
            isInvalidColumn
              ? buildRowValuesBase(row)
              : buildRowValuesWithAudit(row),
          ]
        );
        if (!ins || ins.ok === false || ins.status === "FAILED") {
          const r = {
            ok: false,
            errorCode: "INSERT_SINGLE_FAILED",
            row: row.Item_Code,
            detail: ins,
          };
          getAlert?.("FAILED", `บันทึกสินค้า ${row.Item_Code} ไม่สำเร็จ`);
          return r;
        }
      }
      inserted = codesSql
        ? await getDataWithComp(
            "Tb_Set_Item",
            `AND Comp_Id=${compId} AND Item_Code IN (${codesSql})`
          )
        : [];
    }

    // ✅ ตรวจสอบว่ามี Item_Code ใน details หรือไม่
    const detailsWithCode = details.filter((d) => isNonEmpty(d.Item_Code));
    if (detailsWithCode.length === 0) {
      const r = {
        ok: false,
        errorCode: "NO_ITEM_CODE",
        message: "ไม่มี Item_Code ในรายการที่ส่งมา",
      };
      getAlert?.("FAILED", r.message);
      return r;
    }

    if (!inserted || inserted.length !== detailsWithCode.length) {
      // ✅ ใช้ trim() ให้ consistent กันทั้งสองฝั่ง
      const returnedCodes = new Set(
        (inserted || []).map((r) => String(trim(r.Item_Code || "")))
      );
      const stillMissing = detailsWithCode
        .filter((d) => !returnedCodes.has(String(trim(d.Item_Code || ""))))
        .map((d) => d.Item_Code);

      console.error("INSERT_MISMATCH:", {
        insertedCount: inserted?.length || 0,
        detailsCount: detailsWithCode.length,
        insertedCodes: Array.from(returnedCodes),
        missingCodes: stillMissing,
        allDetailsCodes: detailsWithCode.map((d) => trim(d.Item_Code || "")),
      });

      const r = {
        ok: false,
        errorCode: "INSERT_MISMATCH",
        message: `จำนวนที่เพิ่มไม่ครบ (${inserted?.length || 0}/${
          detailsWithCode.length
        })`,
        missing: stillMissing,
      };
      getAlert?.("FAILED", `${r.message}\nขาด: ${stillMissing.join(", ")}`);
      return r;
    }

    const idByCode = new Map(
      inserted.map((r) => [String(r.Item_Code), Number(r.Item_Id)])
    );

    // ---------- 6) ตั้งต้นสต็อกทุกคลัง ----------
    const whIds = Array.isArray(header.WH_Ids) ? header.WH_Ids : [];
    const DOC_TYPE = "BG";
    const remark = "เริ่มต้นสินค้า";

    if (whIds.length > 0) {
      for (const row of details) {
        const code = String(trim(row.Item_Code));
        const itemId = idByCode.get(code);
        if (!itemId) {
          const r = { ok: false, errorCode: "NO_ITEM_ID", item: code };
          getAlert?.("FAILED", `ไม่พบ Item_Id ของ ${code}`);
          return r;
        }

        const initQtyEach = Number(row.Init_Qty ?? header.Init_Qty_All ?? 0);
        if (initQtyEach >= 0) {
          for (const whId of whIds) {
            const ok = await manageWhItemData(
              itemId,
              code,
              row.Item_Name,
              whId,
              DOC_TYPE,
              initQtyEach,
              null,
              null,
              null,
              remark
            );
            if (ok === null) {
              return {
                ok: false,
                errorCode: "INIT_STOCK_FAILED",
                item: code,
                whId,
              };
            }
          }
        }
      }
    }

    return {
      ok: true,
      count: details.length,
      ids: Object.fromEntries(idByCode),
    };
  } catch (err) {
    console.error("submitItems fatal:", err);
    getAlert?.("FAILED", err?.message || "บันทึกไม่สำเร็จ");
    return { ok: false, errorCode: "UNEXPECTED", error: String(err) };
  }
}

export async function updateItems(rows = []) {
  if (!Array.isArray(rows) || rows.length === 0) return;
  const updateByName = window.localStorage.getItem("name") || "";
  const getSqlDateTime = () => {
    const now = new Date();
    return new Date(now.getTime() - now.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 19)
      .replace("T", " ");
  };

  const getBaseName = (fullName) => {
    if (!fullName) return "";
    let base = fullName;
    base = base.replace(/\s+สี[^\s]+/g, "");
    base = base.replace(
      /\s+(ไซต์\s*)?([SMLX]{1,3}|[0-9]{1,2}XL|[0-9]+|[A-Z]{1,2}S?)\s*$/g,
      ""
    );
    return base.trim();
  };

  for (const r of rows) {
    if (!r.Item_Id) continue;
    const baseName = getBaseName(String(r.Item_Name ?? ""));

    const baseValues = {
        Item_Name: String(r.Item_Name ?? "").trim(),
        Item_Name_EN: String(r.Item_Name_EN ?? r.Item_Name ?? "").trim(),
        Item_Short_Name: baseName,
        Item_Unit_IN: String(r.Item_Unit_IN ?? r.Unit ?? "").trim(),
        Item_Qty: Number(r.Item_Qty ?? 0),
        Item_Is_Sale: String(r.Item_Is_Sale ?? "N"),
        Item_Is_Service: String(r.Item_Is_Service ?? "N"),

        Item_Cost: Number(r.Cost ?? r.Item_Cost ?? 0),
        Item_Price: Number(r.Price1 ?? r.Item_Price ?? 0),
        Item_PriceA: Number(r.Price2 ?? r.Item_PriceA ?? 0),
        Item_PriceB: Number(r.Price3 ?? r.Item_PriceB ?? 0),

        Item_Brand: String(r.Brand ?? r.Item_Brand ?? "").trim(),
        Item_Series: String(r.Series ?? r.Item_Series ?? "").trim(),
        Item_Status: String(r.StatusYN ?? r.Item_Status ?? "Y"),
      };
    const withAudit = {
      ...baseValues,
      Update_Date: getSqlDateTime(),
      ...(updateByName ? { Update_By_Name: updateByName } : {}),
    };
    const res = await updateData("Tb_Set_Item", withAudit, { Item_Id: r.Item_Id });
    if (res?.status === "FAILED" && /Invalid column name/i.test(res?.message || "")) {
      await updateData("Tb_Set_Item", baseValues, { Item_Id: r.Item_Id });
    }
  }
}

/**
 * ตรวจสอบว่าสินค้ามีการเคลื่อนไหวสต็อกหรือไม่
 * @param {Array} itemIds - Array of Item_Id ที่ต้องการตรวจสอบ
 * @returns {Promise<Array>} - Array of Item_Id ที่มีการเคลื่อนไหวสต็อก
 */
async function checkItemStockMovement(itemIds = []) {
  if (!Array.isArray(itemIds) || itemIds.length === 0) return [];

  try {
    const ids = itemIds.filter((id) => id != null && id !== undefined);
    if (ids.length === 0) return [];

    // ดึงข้อมูลการเคลื่อนไหวทั้งหมด
    const movements = await getDataWithComp(
      "WH_ITEM_STC",
      `AND Item_Id IN (${ids.join(",")})`
    );

    if (!movements || movements.length === 0) return [];

    const undeletableIds = new Set();

    for (const m of movements) {
      const itemId = Number(m.Item_Id);
      const docType = String(m.Doc_Type || "").trim();
      const balance = Number(m.STC_Balance || 0);

      // ✅ กรณี BG (Begin Balance): ห้ามลบเฉพาะถ้ายังมียอดคงเหลือ (STC_Balance > 0)
      if (docType === "BG") {
        if (balance > 0) {
          undeletableIds.add(itemId);
        }
        continue;
      }

      // ✅ กรณีอื่น ๆ (IN, OUT, AI, AO, TI, TO, CNT): ห้ามลบทันที
      if (["IN", "OUT", "AI", "AO", "TI", "TO", "CNT"].includes(docType)) {
        undeletableIds.add(itemId);
      }
    }

    return Array.from(undeletableIds);
  } catch (err) {
    console.error("checkItemStockMovement error:", err);
    return [];
  }
}

/**
 * ตรวจสอบว่าสินค้ามียอดคงเหลือในคลังหรือไม่
 * @param {Array} itemIds - Array of Item_Id ที่ต้องการตรวจสอบ
 * @returns {Promise<Array>} - Array of Item_Id ที่มียอดคงเหลือ > 0
 */
async function checkItemOnhand(itemIds = []) {
  if (!Array.isArray(itemIds) || itemIds.length === 0) return [];

  try {
    const ids = itemIds.filter((id) => id != null && id !== undefined);
    if (ids.length === 0) return [];

    // ตรวจสอบจาก WH_ITEM_Onhand ว่ามียอดคงเหลือ > 0
    const onhand = await getDataWithComp(
      "WH_ITEM_Onhand",
      `AND Item_Id IN (${ids.join(",")}) AND Item_Onhand > 0`
    );

    if (!onhand || onhand.length === 0) return [];

    // คืน Item_Id ที่มียอดคงเหลือ (unique)
    const itemsWithOnhand = [...new Set(onhand.map((o) => Number(o.Item_Id)))];
    return itemsWithOnhand;
  } catch (err) {
    console.error("checkItemOnhand error:", err);
    return [];
  }
}

export async function deleteItems(items = [], options = {}) {
  const opts = {
    checkOnly: false, // ✅ เช็คอย่างเดียว ห้ามลบจริง
    silent: false, // ไม่ alert
    skipConfirmation: false, // คงไว้เพื่อ backward compat (ไม่ใช้ใน service)
    ...options,
  };

  // ------------------------------
  // 0) normalize input -> [{Item_Id, Item_Code, Item_Name}]
  // ------------------------------
  const list = Array.isArray(items) ? items : [];
  const normalized = list
    .map((x) => {
      const Item_Id = x?.Item_Id ?? x?.id ?? x;
      return {
        Item_Id,
        Item_Code: x?.Item_Code,
        Item_Name: x?.Item_Name,
      };
    })
    .filter((x) => x.Item_Id != null && x.Item_Id !== "");

  // ไม่มีอะไรให้ทำ
  if (normalized.length === 0) {
    return { canDelete: [], cannotDelete: [] };
  }

  // ------------------------------
  // 1) ✅ CHECK: แยก canDelete / cannotDelete จาก stock movement
  // ------------------------------
  let canDelete = [];
  let cannotDelete = [];

  try {
    // แปลง Item_Id เป็น number สำหรับเช็ค movement
    const ids = normalized
      .map((x) => Number(x.Item_Id))
      .filter((n) => Number.isFinite(n));

    // ถ้า ids ใช้เช็คไม่ได้เลย (เช่นเป็น string แปลก ๆ) ให้กันไว้ไม่ลบ
    if (ids.length === 0) {
      cannotDelete = [...normalized];
      canDelete = [];
    } else {
      // ✅ เช็คทั้งการเคลื่อนไหวสต็อกและยอดคงเหลือ
      const [itemsWithMovement, itemsWithOnhand] = await Promise.all([
        checkItemStockMovement(ids),
        checkItemOnhand(ids),
      ]);

      const cannotDeleteSet = new Set([
        ...(itemsWithMovement || []).map((n) => Number(n)),
        ...(itemsWithOnhand || []).map((n) => Number(n)),
      ]);

      cannotDelete = normalized.filter((x) =>
        cannotDeleteSet.has(Number(x.Item_Id))
      );
      canDelete = normalized.filter(
        (x) => !cannotDeleteSet.has(Number(x.Item_Id))
      );
    }
  } catch (err) {
    console.error("deleteItems check error:", err);
    // ✅ fail-safe: ถ้าเช็คพัง “ไม่ให้ลบ”
    cannotDelete = [...normalized];
    canDelete = [];
  }

  // เผื่อกรณีแปลก ๆ
  if (!Array.isArray(canDelete)) canDelete = [];
  if (!Array.isArray(cannotDelete)) cannotDelete = [];

  // ------------------------------
  // 2) ✅ checkOnly: คืนผลอย่างเดียว “ห้ามลบจริง”
  // ------------------------------
  if (opts.checkOnly) {
    return { canDelete, cannotDelete };
  }

  // ------------------------------
  // 3) ✅ ลบจริงเฉพาะรายการที่ลบได้
  // ------------------------------
  if (canDelete.length > 0) {
    for (const r of canDelete) {
      const where = `WHERE Item_Id='${r.Item_Id}'`;
      await Promise.all([
        deleteData("Tb_Set_Item", where),
        deleteData("WH_ITEM_STC", where),
        deleteData("WH_ITEM_Onhand", where),
      ]);
    }
  }

  // ------------------------------
  // 4) แจ้งผล (ถ้าไม่ silent)
  // ------------------------------
  if (!opts.silent) {
    if (cannotDelete.length > 0 && canDelete.length === 0) {
      getAlert(
        "FAILED",
        "ไม่สามารถลบสินค้าได้ เนื่องจากมีการเคลื่อนไหวสต็อกแล้ว"
      );
    } else if (cannotDelete.length > 0 && canDelete.length > 0) {
      getAlert(
        "WARNING",
        "มีบางรายการลบไม่ได้ (มีการเคลื่อนไหวสต็อกแล้ว) แต่รายการที่ลบได้ถูกลบแล้ว"
      );
    } else if (canDelete.length > 0) {
      getAlert("OK", `ลบข้อมูลสำเร็จ ${canDelete.length} รายการ`);
    }
  }

  return { canDelete, cannotDelete };
}

export async function deleteItem(item, options = {}) {
  const opts = {
    silent: false, // ไม่เด้ง alert ถ้า true
    confirm: false, // ถ้าจะให้ service ถามเอง (ปกติให้ UI ถามอยู่แล้ว)
    confirmText: "คุณต้องการลบรายการนี้หรือไม่?",
    ...options,
  };

  const Item_Id = item?.Item_Id ?? item?.id ?? item;
  if (!Item_Id) {
    if (!opts.silent) getAlert?.("FAILED", "ไม่พบ Item_Id สำหรับการลบ");
    return { ok: false, canDelete: [], cannotDelete: [] };
  }

  // 1) ✅ เช็คการเคลื่อนไหวสต็อกและยอดคงเหลือ
  let movedIds = [];
  let onhandIds = [];
  try {
    [movedIds, onhandIds] = await Promise.all([
      checkItemStockMovement([Item_Id]),
      checkItemOnhand([Item_Id]),
    ]);
  } catch (e) {
    console.error("checkItemStockMovement/Onhand error:", e);
    if (!opts.silent) getAlert?.("FAILED", "ตรวจสอบสต็อกไม่สำเร็จ");
    return { ok: false, canDelete: [], cannotDelete: [{ Item_Id }] };
  }

  const hasMovement =
    Array.isArray(movedIds) &&
    movedIds.some((x) => String(x) === String(Item_Id));

  const hasOnhand =
    Array.isArray(onhandIds) &&
    onhandIds.some((x) => String(x) === String(Item_Id));

  if (hasMovement || hasOnhand) {
    // ลบไม่ได้
    if (!opts.silent) {
      const reason = hasMovement
        ? "มีการเคลื่อนไหวสต็อกแล้ว"
        : "มียอดคงเหลือในคลัง";
      getAlert?.("WARNING", `ไม่สามารถลบสินค้าได้ เนื่องจาก${reason}`);
    }
    return {
      ok: false,
      canDelete: [],
      cannotDelete: [
        { Item_Id, Item_Code: item?.Item_Code, Item_Name: item?.Item_Name },
      ],
    };
  }

  // 2) ✅ ถ้าจะให้ service ถามยืนยันเอง (ปกติ UI ถามแล้ว ไม่ต้องเปิด)
  const doDelete = async () => {
    const where = `WHERE Item_Id='${Item_Id}'`;
    await Promise.all([
      deleteData("Tb_Set_Item", where),
      deleteData("WH_ITEM_STC", where),
      deleteData("WH_ITEM_Onhand", where),
    ]);
    return {
      ok: true,
      canDelete: [
        { Item_Id, Item_Code: item?.Item_Code, Item_Name: item?.Item_Name },
      ],
      cannotDelete: [],
    };
  };

  if (opts.confirm) {
    return new Promise((resolve) => {
      getAlert?.("C_DIALOG", opts.confirmText, async (ok) => {
        if (!ok) return resolve({ ok: false, canDelete: [], cannotDelete: [] });
        try {
          const res = await doDelete();
          if (!opts.silent) getAlert?.("OK", "ลบรายการสำเร็จ");
          resolve(res);
        } catch (e) {
          console.error("deleteItem error:", e);
          if (!opts.silent) getAlert?.("FAILED", "ไม่สามารถลบรายการได้");
          resolve({ ok: false, canDelete: [], cannotDelete: [] });
        }
      });
    });
  }

  // 3) ✅ ลบจริง (UI ถาม confirm แล้ว)
  try {
    const res = await doDelete();
    if (!opts.silent) getAlert?.("OK", "ลบรายการสำเร็จ");
    return res;
  } catch (e) {
    console.error("deleteItem error:", e);
    if (!opts.silent) getAlert?.("FAILED", "ไม่สามารถลบรายการได้");
    return { ok: false, canDelete: [], cannotDelete: [] };
  }
}

/**
 * ✅ Bulk update items ที่มี Item_Code_Master เดียวกัน
 * @param {Object} updates - { Item_Name, Item_Brand, Item_Series, Item_Group }
 * @param {string} itemCodeMaster - Item_Code_Master ที่ต้องการอัปเดต
 * @param {Object} options - { silent: boolean }
 * @returns {Promise<{ok: boolean, affectedItems?: array, count?: number}>}
 */
export async function bulkUpdateByCodeMaster(
  updates = {},
  itemCodeMaster,
  options = {}
) {
  const opts = {
    silent: false,
    ...options,
  };

  if (!itemCodeMaster) {
    if (!opts.silent) getAlert?.("FAILED", "ไม่พบ Item_Code_Master");
    return { ok: false };
  }

  const compId = window.localStorage.getItem("company");
  if (!compId) {
    if (!opts.silent) getAlert?.("FAILED", "ไม่พบ Company ID");
    return { ok: false };
  }

  try {
    // ✅ ถ้ามีการเปลี่ยนชื่อ → ต้องอัปเดตทีละรายการ (เพื่อรักษาสี/ไซส์)
    if (updates.Item_Name != null) {
      const items = await getItemsByCodeMaster(itemCodeMaster);

      const getBaseName = (fullName) => {
        if (!fullName) return "";
        let base = fullName;
        base = base.replace(/\s+สี[^\s]+/g, "");
        base = base.replace(
          /\s+(ไซต์\s*)?([SMLX]{1,3}|[0-9]{1,2}XL|[0-9]+|[A-Z]{1,2}S?)\s*$/g,
          ""
        );
        return base.trim();
      };

      const updateByName = window.localStorage.getItem("name") || "";
      const now = new Date();
      const updateDate = new Date(
        now.getTime() - now.getTimezoneOffset() * 60000
      )
        .toISOString()
        .slice(0, 19)
        .replace("T", " ");
      const safeUpdateItem = async (values, where) => {
        const res = await updateData("Tb_Set_Item", values, where);
        if (res?.status === "FAILED" && /Invalid column name/i.test(res?.message || "")) {
          const { Update_Date, Update_By_Name, ...fallback } = values;
          await updateData("Tb_Set_Item", fallback, where);
        }
      };

      const newBaseName = String(updates.Item_Name).trim();

      // ✅ อัปเดตทีละรายการ แทนที่ชื่อฐาน แต่รักษาสี/ไซส์
      for (const item of items) {
        const oldFullName = String(item.Item_Name || "");
        const oldBaseName = getBaseName(oldFullName);

        // แทนที่ชื่อฐานเก่าด้วยชื่อใหม่ (รักษาสี/ไซส์)
        const newFullName = oldFullName
          .replace(oldBaseName, newBaseName)
          .trim();

        await safeUpdateItem(
          {
            Item_Name: newFullName,
            Item_Short_Name: newBaseName,
            Update_Date: updateDate,
            ...(updateByName ? { Update_By_Name: updateByName } : {}),
          },
          { Item_Id: item.Item_Id }
        );
      }
    }

    // ✅ ฟิลด์อื่นๆ (Brand, Series, Group) ยังใช้ bulk update ได้ตามเดิม
    const updateFields = {
      Update_Date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 19)
        .replace("T", " "),
      ...(window.localStorage.getItem("name")
        ? { Update_By_Name: window.localStorage.getItem("name") }
        : {}),
    };

    if (updates.Item_Brand != null) {
      updateFields.Item_Brand = String(updates.Item_Brand).trim();
    }
    if (updates.Item_Series != null) {
      updateFields.Item_Series = String(updates.Item_Series).trim();
    }
    if (updates.Item_Group != null) {
      updateFields.Item_Group = Number(updates.Item_Group);
    }

    // ถ้ามีฟิลด์อื่นที่ต้อง update
    if (Object.keys(updateFields).length > 0) {
      const res = await updateData("Tb_Set_Item", updateFields, {
        Item_Code_Master: itemCodeMaster,
        Comp_Id: compId,
      });
      if (res?.status === "FAILED" && /Invalid column name/i.test(res?.message || "")) {
        const { Update_Date, Update_By_Name, ...fallback } = updateFields;
        if (Object.keys(fallback).length > 0) {
          await updateData("Tb_Set_Item", fallback, {
            Item_Code_Master: itemCodeMaster,
            Comp_Id: compId,
          });
        }
      }
    }

    return { ok: true };
  } catch (err) {
    console.error("bulkUpdateByCodeMaster error:", err);
    if (!opts.silent) getAlert?.("FAILED", "อัปเดตข้อมูลไม่สำเร็จ");
    return { ok: false, error: String(err) };
  }
}

/**
 * ✅ ดึงรายการทั้งหมดที่มี Item_Code_Master เดียวกัน
 * @param {string} itemCodeMaster - Item_Code_Master ที่ต้องการค้นหา
 * @returns {Promise<array>} - รายการสินค้าทั้งหมด
 */
export async function getItemsByCodeMaster(itemCodeMaster) {
  if (!itemCodeMaster) return [];

  try {
    const items = await getDataWithComp(
      "Tb_Set_Item",
      `AND Item_Code_Master = ${q(itemCodeMaster)}`
    );
    return items || [];
  } catch (err) {
    console.error("getItemsByCodeMaster error:", err);
    return [];
  }
}
